import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ENUM_ClientUserCreationMethod } from '@api-new/common';
import { ENUM_SmartSearchCaseStatus } from '@api-new/common/ENUM_SmartSearchCaseStatus';
import { DesktopWelcomeModalComponent } from '@platform/dashboard/components/desktop-welcome-modal/desktop-welcome-modal.component';
import { MobileWelcomeModalComponent } from '@platform/dashboard/components/mobile-welcome-modal/mobile-welcome-modal.component';
import { welcomeScreenScenariosMap } from '@platform/dashboard/components/welcome-modal/welcome-screen-data';
import { ScenarioType } from '@platform/dashboard/enums/scenario-type.enum';
import { WelcomeScreenInputModel } from '@platform/dashboard/models/welcome-screen-template-model.model';
import {
  ScreenSizeBreakpointNames,
  ScreenSizeBreakpointObserverService,
} from '@shared-lib/services/screen-size-breakpoint-observer.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'cp-welcome-modal',
  templateUrl: './welcome-modal.component.html',
  styleUrls: ['./welcome-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DesktopWelcomeModalComponent, MobileWelcomeModalComponent, AsyncPipe],
})
export class WelcomeModalComponent implements OnInit {
  @Input() set welcomeModalData(welcomeModalData: WelcomeScreenInputModel) {
    this._welcomeModalData = welcomeModalData;
    if (welcomeModalData.creationMethod) {
      this.creationMethod = welcomeModalData.creationMethod;
    }
    if (welcomeModalData.clientUserData) {
      this.selectToSpeakWithAdviser = this.hasActiveSmartSearch(welcomeModalData.clientUserData);
    }
    this.setWelcomeModalScenario();
    // Do additional processing or trigger events here if needed
  }

  get welcomeModalData(): WelcomeScreenInputModel {
    return this._welcomeModalData;
  }

  @Output() skipButtonClick = new EventEmitter();

  private readonly breakpointObserverService = inject(ScreenSizeBreakpointObserverService);

  private _welcomeModalData: WelcomeScreenInputModel;

  creationMethod = ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_BUY_TO_LET;
  selectToSpeakWithAdviser = false;
  modalTemplateData;
  size$: Observable<string>;
  BreakpointNames = ScreenSizeBreakpointNames;

  ngOnInit(): void {
    this.size$ = this.breakpointObserverService.screenSizeBreakpointName$;
  }

  cancelTutorials(): void {
    this.skipButtonClick.emit();
  }

  hasActiveSmartSearch(smartSearches): boolean {
    return smartSearches.some(
      (smartSearch) => smartSearch.smartSearchCase?.smartSearchCaseStatus === ENUM_SmartSearchCaseStatus.SMART_SEARCH_CASE_STATUS_PIPELINE,
    );
  }

  setWelcomeModalScenario(): void {
    if (this.welcomeModalData.isExperianCompany) {
      this.modalTemplateData = welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_EXPERIAN);
      return;
    }
    switch (this.creationMethod) {
      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_UNSPECIFIED:
      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_CLIENT_USER_IMPORT:
      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_BY_PROXY:
      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_REGISTRATION:
      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_MORTGAGE_MONITOR:
        this.modalTemplateData = welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_1);
        break;

      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_REMORTGAGE_NOW:
        this.modalTemplateData = this.selectToSpeakWithAdviser
          ? welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_2)
          : welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_1);
        break;

      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_FIRST_TIME_BUYER:
        this.modalTemplateData = this.selectToSpeakWithAdviser
          ? welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_2)
          : welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_3);
        break;

      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_MOVING_HOME:
        this.modalTemplateData = this.selectToSpeakWithAdviser
          ? welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_2)
          : welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_1);
        break;

      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_BUY_TO_LET:
        this.modalTemplateData = this.selectToSpeakWithAdviser
          ? welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_2)
          : welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_3);
        break;

      case ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_LANDLORD:
        this.modalTemplateData = welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_4);
        break;

      default:
        this.modalTemplateData = welcomeScreenScenariosMap.get(ScenarioType.SCENARIO_1);
        break;
    }
  }
}
